import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";

import LoaderContainer from "../components/LoaderContainer";
import api from "../globals/axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../components/Alert";
import CustomAppBar from "../components/AppBar";
import { CustomTitle } from "../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import GenericButton from "../components/Custom/GenericButton";

const VLETransfer = ({ role }) => {
  const { id } = useParams();
  const [vles, setVles] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [farmer, setFarmer] = useState({});
  const [selectedVLE, setSelectedVLE] = useState("");
  const [selectedAreaManager, setSelectedAreaManager] = useState("");
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  useEffect(() => {
    fetchFarmer(id);
    if (role == "POP_MANAGER") {
      fetchAreaManagers();
    } else {
      fetchVLEs();
    }
  }, [id, role]);

  useEffect(() => {
    if (role == "POP_MANAGER" && selectedAreaManager) {
      if (farmer.area_manager_id !== selectedAreaManager) {
        setSelectedVLE("");
      }
      fetchVLEs(selectedAreaManager);
    }
  }, [selectedAreaManager]);

  const fetchAreaManagers = async () => {
    try {
      const res = await api.get(
        `/usermanagement/get-sub-users?area_managers=true`
      );
      setAreaManagers(res.data.data.area_managers);
    } catch (error) {
      console.error("Error fetching Area Managers:", error);
    }
  };

  const fetchVLEs = async (area_manager_id = null) => {
    try {
      const endpoint =
        role === "POP_MANAGER"
          ? `/usermanagement/get-sub-users?area_manager_id=${area_manager_id}&vles=true`
          : `/usermanagement/get-sub-users?vles=true`;
      const res = await api.get(endpoint);
      setVles(res.data.data.vles);
    } catch (error) {
      console.error("Error fetching VLEs:", error);
    }
  };

  const fetchFarmer = async (id) => {
    try {
      const { data } = await api.get(
        `/app-admin/get-farmer-details-by-id/${id}`
      );
      setFarmer(data.data);
      setSelectedVLE(data.data?.vle_id || "");

      if (role === "POP_MANAGER") {
        setSelectedAreaManager(data.data?.area_manager_id || "");
        fetchVLEs(data.data?.area_manager_id);
      }
    } catch (error) {
      console.error("Error fetching farmer:", error);
    }
  };

  const onTransfer = async () => {
    const formData = new FormData();
    formData.append("new_vle_id", selectedVLE);
    formData.append("farmer_id", farmer.farmer_id);
    if (role === "POP_MANAGER")
      formData.append("new_area_manager_id", selectedAreaManager);

    try {
      const res = await api.post(
        `/usermanagement/transfer-area-vle-farmer/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      showAlert("success", res.data.message);
      navigate("/farmers-list");
    } catch (error) {
      console.error("Error:", error.response?.data.message || "Request failed");
    }
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={false}>
        <Container maxWidth="xs">
          <CustomTitle>{t("Transfer Farmer")}</CustomTitle>

          {/* Farmer Details */}
          <Box mb={3} mt={2}>
            <Typography variant="body1" fontWeight="bold">
              {t("Farmer Details")} -
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 2 }}>
              <strong>{t("Name")}:</strong> {farmer.farmer_name || ""}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 1 }}>
              <strong>{t("Mobile")}:</strong> {farmer.mobile || ""}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 1 }}>
              <strong>{t("Address")}:</strong> {farmer.village || ""}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 1 }}>
              <strong>{t("Current VLE")}:</strong> {farmer.vle || ""}
            </Typography>
            {role === "POP_MANAGER" && (
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                <strong>{t("Current AM")}:</strong> {farmer.area_manager}
              </Typography>
            )}
          </Box>
          <Box>
            {role === "POP_MANAGER" && (
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <InputLabel>{t("Area Managers")}</InputLabel>
                <Select
                  value={selectedAreaManager}
                  onChange={(e) => setSelectedAreaManager(e.target.value)}
                >
                  {areaManagers.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth sx={{ marginTop: 2 }}>
              <InputLabel>{t("VLE")}</InputLabel>
              <Select
                value={selectedVLE}
                onChange={(e) => setSelectedVLE(e.target.value)}
                sx={{ borderRadius: 2, backgroundColor: "white" }}
              >
                {vles.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={3}>
              <GenericButton
                backgroundColor={"#4CAF50"}
                fontColor={"#fff"}
                fontSize={"14px"}
                padding={"7px 10px"}
                onClick={() => onTransfer()}
              >
                {t("Transfer")}
              </GenericButton>
            </Box>
          </Box>
        </Container>
      </LoaderContainer>
    </>
  );
};

export default VLETransfer;
